import type { IfAdvanceSettingForm, IfGeneralSettingForm, IfLayoutSettingForm } from './types';
import { mutations as TYPES, TemplateHandleType } from './types';
import { State } from './state';
import { FETCH } from '@/utils/list';
import { showHeaderFooter } from '@/utils/headerFooterHelper';
import { mutations as MUTATION } from './types';

const comparer = (obj: any, item: any) => {
  return obj.id === item.id;
};

const mutations = {
  /**
   * Reset
   * @param state State
   */
  [TYPES.RESET](state: State) {
    state = new State();
  },

  [TYPES.COMMIT_PAGES]: (state: State, { pages, type }: { pages: any[]; type: string }) => {
    FETCH((state as any)[type].list, pages, comparer, undefined);
  },

  UPDATE_PAGE_SETTING_FORM: (state: State, { tab, field, value }: { tab: string; field: string; value: any }) => {
    (state.pageSettingForm as any)[tab][field] = value;

    // check change
    const defaultValue = (state.defaultPageSettingForm as any)[tab][field];
    (state.pageSettingChange as any)[field] = value != defaultValue;
  },
  PAGE_SETTING_IS_ERROR: (state: State, isError = false) => {
    state.pageSettingIsError = isError;
  },
  ADD_SETTING_CHANGED_LIST: (state: State, pageId: number) => {
    const list = state.updatedSettingPageList;
    if (list.includes(pageId)) return;
    state.updatedSettingPageList.push(pageId);
  },
  REMOVE_SETTING_CHANGED_LIST: (state: State, pageId: number) => {
    const list = state.updatedSettingPageList;
    const index = list.indexOf(pageId);
    if (index !== -1) list.splice(index, 1);
  },
  SET_DEFAULT_PAGE_SETTING_FORM: (
    state: State,
    { newValue, pageType, pageGID }: { newValue: any; pageType: string; pageGID: string },
  ) => {
    let handle = newValue.general.handle;
    let description = newValue.general.description;

    if (pageType && pageGID) {
      if (!handle) {
        handle = `${TemplateHandleType[pageType]}-${pageGID}`;
      }

      if (!description && TemplateHandleType[pageType]) {
        description = `${pageType} meta`;
      }
    }

    const general: IfGeneralSettingForm = {
      title: newValue.general.title,
      handle,
      description,
      featuredImage: newValue.general.featured_image,
    };

    if (newValue.general.blog_handle) {
      state.blogHandle = newValue.general.blog_handle as string;
    }

    const layout: IfLayoutSettingForm = {
      headerFooter: newValue.layout.header_footer,
    };

    const advance: IfAdvanceSettingForm = {
      lazyLoad: newValue.advance.lazy_load,
    };

    showHeaderFooter(newValue.layout.header_footer);

    const form = {
      general,
      layout,
      advance,
    };

    //deep clone
    const formString = JSON.stringify(form);

    state.defaultPageSettingForm = JSON.parse(formString);
    state.pageSettingForm = JSON.parse(formString);

    // state.resetPageSettingChange();
  },

  SET_DEFAULT_PAGE_TITLE: (state: State, newTitle: string) => {
    state.pageSettingForm.general.title = newTitle;
    state.defaultPageSettingForm.general.title = newTitle;
    state.pageSettingChange.title = false;
  },

  SET_DEFAULT_LAZY_LOADING: (state: State, enableLazyLoad: boolean) => {
    state.pageSettingForm.advance.lazyLoad = enableLazyLoad;
    state.defaultPageSettingForm.advance.lazyLoad = enableLazyLoad;
    state.pageSettingChange.lazyLoad = false;
  },

  SET_DEFAULT_FRAME_LOADING: (state: State, isLoading: boolean) => {
    state.loadingFrame = isLoading;
  },

  SET_LOADING_CONTENT_EDITOR: (state: State, isLoading: boolean) => {
    state.loadingContentEditor = isLoading;
  },

  IS_PRODUCT_NOT_FOUND: (state: State, value: boolean) => {
    state.isProductNotFound = value;
  },

  SET_FRAME_LOADING_MESSAGE: (state: State, message: string) => {
    state.loadingFrameMessage = message;
  },

  SET_DEFAULT_GENERAL_PAGE_SETTING: (state: State, { newValue, pageType, pageGID }: any) => {
    const title = newValue.title ?? state.pageSettingForm.general.title;
    let handle = newValue.handle;
    let description = newValue.description;
    const featuredImage = newValue.featuredImage ?? state.pageSettingForm.general.featuredImage;

    if (pageType && pageGID) {
      if (!handle) {
        handle = `${TemplateHandleType[pageType]}-${pageGID}`;
      }

      if (!description && TemplateHandleType[pageType]) {
        description = `${pageType} meta`;
      }
    }

    const newGeneral: IfGeneralSettingForm = {
      title,
      handle,
      description,
      featuredImage,
    };

    //deep clone
    const newGeneralString = JSON.stringify(newGeneral);

    state.defaultPageSettingForm.general = JSON.parse(newGeneralString);
    state.pageSettingForm.general = JSON.parse(newGeneralString);

    const pageSettingChange = state.pageSettingChange;

    // reset change flags
    state.pageSettingChange = {
      ...pageSettingChange,
      title: newValue.title ? false : pageSettingChange.title,
      handle: newValue.handle ? false : pageSettingChange.handle,
      description: newValue.description ? false : pageSettingChange.description,
      featuredImage: newValue.featuredImage ? false : pageSettingChange.featuredImage,
    };
  },
  [TYPES.UPDATE_PAGE_TITLE](state: State, title: string) {
    state.pageSettingForm.general.title = title;

    return (state.defaultPageSettingForm.general.title = title);
  },
  [MUTATION.RESET_SEARCH_RESULT](state: State) {
    const idProductDynamic = state.assignedProduct.idProductDynamic;
    const list = state.assignedProduct.list;
    const listQuery = state.assignedProduct.listQuery;
    let idx = list.findIndex((a: any) => a.id === idProductDynamic);
    if (idx == -1) {
      idx = listQuery.findIndex((a: any) => a.id === idProductDynamic);
      if (idx !== -1 && listQuery[idx]) {
        state.assignedProduct.list.unshift(listQuery[idx]);
      }
    }
    state.assignedProduct.listQuery = [];
    state.assignedProduct.fetchedAllQuery = false;
  },
  [MUTATION.RESET_ASSIGNED_DATA](state: State) {
    return state.resetAssignedProduct();
  },
  [MUTATION.SET_ID_PRODUCT_DYNAMIC](state: State, id: any) {
    state.assignedProduct.idProductDynamic = id;
  },
  [MUTATION.SET_STATUS_PRODUCT_DYNAMIC](state: State, status: any) {
    state.assignedProduct.statusProductDynamic = status;
  },
  [MUTATION.SET_POPUP_ASSIGNED_PRODUCT_ACTIVE](state: State, status: boolean) {
    state.assignedProduct.popupAssignedProductActive = status;
  },
  [MUTATION.SET_IS_SHOW_MESSAGE_PUBLISH_POPUP](state: State, status: boolean) {
    state.assignedProduct.isShowMessagePublishPopup = status;
  },
  [MUTATION.UPDATE_IS_ASSIGN_PRODUCT_MODEL](state: State, value: boolean) {
    state.assignedProduct.isAssignProductModal = value;
  },
  [MUTATION.UPDATE_IS_INIT_PRELOAD_PRODUCT_SNIPPET](state: State, value: boolean) {
    state.assignedProduct.isInitPreloadProductSnippet = value;
  },
  [MUTATION.UPDATE_IS_USED_PRELOAD_PRODUCT_SNIPPET](state: State, value: boolean) {
    state.assignedProduct.isUsedPreloadProductSnippet = value;
  },
  [MUTATION.UPDATE_COUNT_PRODUCT_DYNAMIC](state: State) {
    state.assignedProduct.countProductDynamic++;
  },
  [MUTATION.RESET_COUNT_PRODUCT_DYNAMIC](state: State) {
    state.assignedProduct.countProductDynamic = 0;
  },
};

export default mutations;
